import React, { useState, useRef } from "react"
import readXlsxFile from "read-excel-file"
import BaseApi from "../../api/api"
import { NotificationService } from "../../features/NotificationService/NotificationService"
import { selectWbContent } from "../../app/reducers/contentSlice"
import { useSelector } from "react-redux"
import { StatusInfo } from "../UpdateStoksIcon/StatusInfo/StatusInfo"
import { Modal } from "../../features/Modal/Modal"
import { ObjectViewer } from "../ObjectViewer/ObjectViewer"
import { STATUS } from "../../utils/const"
import { getMessage } from "../../msgs/msgs"
import {
  Box,
  Flex,
  Button,
  Card,
  Badge,
  Checkbox,
  Heading,
  Text,
  IconButton,
  ScrollArea,
} from "@radix-ui/themes"
import { Cross1Icon, UploadIcon, PlusIcon } from "@radix-ui/react-icons"
import { formatExcelDate } from "../../utils/formatExcelDate"
import { Select } from "../Select/Select"
import {
  DOC_FIELDS,
  DOC_DATE_FIELDS,
  WB_DOCUMENT_TYPES,
} from "../../utils/wbDocumentTypes"
import type { DocField } from "../../utils/wbDocumentTypes"

interface ExcelData {
  [key: string]: any[]
}

interface SelectedColumns {
  article: string
  [key: string]: string
}

interface CardData {
  article: string | number
  documents?: CardDocument[]
  excludeDocuments?: boolean
  overwriteBarcodes?: boolean
  [key: string]: string | number | CardDocument[] | boolean | undefined
}

interface CardDocument {
  type: number
  number?: string
  productNumber?: string
  tradeName?: string
  applicant?: string
  startDate?: string
  endDate?: string
  isEndless?: boolean
}

/** Блок документа в UI: тип (константа) + маппинг колонок Excel на поля документа */
interface DocumentBlock {
  /** Локальный ключ для React */
  id: string
  /** "1" | "2" | ... — константа блока, из Excel не маппится */
  type: string
  columns: Record<DocField, string>
  /** Чекбокс «Бессрочный»: дата окончания не запрашивается */
  isEndless: boolean
}

const CARD_FIELDS: string[] = [
  "description",
  "brand",
  "name",
  "tnved",
  "vat",
  "length",
  "width",
  "height",
  "weightBrutto",
  "tradeName",
  "kizMarked",
  "barcode",
  "gtin",
]

const initialSelectedColumns = (): SelectedColumns => ({
  article: "",
  ...Object.fromEntries(CARD_FIELDS.map((f) => [f, ""])),
})

const emptyColumns = (): Record<DocField, string> =>
  Object.fromEntries(DOC_FIELDS.map((f) => [f, ""])) as Record<DocField, string>

const createDocumentBlock = (): DocumentBlock => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type: "1",
  columns: emptyColumns(),
  isEndless: false,
})

/** Подпись поля документа в превью */
const docFieldLabel = (field: DocField): string => {
  const labels: Record<DocField, string> = {
    number: "Номер документа",
    productNumber: "Доп. номер документа",
    tradeName: "Торговое наименование",
    applicant: "Представитель изготовителя",
    startDate: "Дата начала",
    endDate: "Дата окончания",
  }
  return labels[field]
}

export const UpdateWbCards = () => {
  const [file, setFile] = useState<File | null>(null)
  const [columns, setColumns] = useState<string[]>([])
  const [selectedColumns, setSelectedColumns] = useState<SelectedColumns>(
    initialSelectedColumns(),
  )
  const excelDataRef = useRef<any>(null)
  const [cardData, setCardData] = useState<CardData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [availableFields, setAvailableFields] = useState<string[]>([
    ...CARD_FIELDS,
  ])
  const [selectedFieldType, setSelectedFieldType] = useState<string>("")
  const [showColumnSelect, setShowColumnSelect] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [documents, setDocuments] = useState<DocumentBlock[]>([])
  const [excludeDocuments, setExcludeDocuments] = useState(false)
  const [overwriteBarcodes, setOverwriteBarcodes] = useState(false)
  /** id блока документа, для которого сейчас выбирается колонка */
  const [docColumnTarget, setDocColumnTarget] = useState<{
    blockId: string
    field: DocField
  } | null>(null)

  const wbContentState = useSelector(selectWbContent)

  const [isModalOpen, setModalOpen] = useState(false)

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return

    setFile(selectedFile)
    setIsLoading(true)

    try {
      const data = await readXlsxFile(selectedFile)

      if (data.length > 0) {
        const headers = data[0] as string[]
        setColumns(headers)

        const excelData: ExcelData = {}
        headers.forEach((header, index) => {
          excelData[header] = data.slice(1).map((row) => row[index])
        })

        excelDataRef.current = excelData
      }
    } catch (error) {
      console.error("Ошибка при чтении файла:", error)
      NotificationService.showNotification({
        message: "Ошибка при чтении файла Excel",
        level: "error",
        success: false,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleFieldTypeSelect = (fieldType: string) => {
    setSelectedFieldType(fieldType)
    setShowColumnSelect(true)
  }

  const handleColumnSelect = (columnName: string) => {
    if (selectedFieldType && columnName) {
      setSelectedColumns((prev) => ({
        ...prev,
        [selectedFieldType]: columnName,
      }))

      setAvailableFields((prev) =>
        prev.filter((field) => field !== selectedFieldType),
      )

      setSelectedFieldType("")
      setShowColumnSelect(false)
    }
  }

  const handleAttachClick = () => {
    fileInputRef.current?.click()
  }

  const clearData = () => {
    setFile(null)
    setColumns([])
    setSelectedColumns(initialSelectedColumns())
    setCardData([])
    setAvailableFields([...CARD_FIELDS])
    setSelectedFieldType("")
    setShowColumnSelect(false)
    setDocuments([])
    setExcludeDocuments(false)
    setOverwriteBarcodes(false)
    setDocColumnTarget(null)
    excelDataRef.current = null
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const removeFieldMapping = (fieldType: string) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [fieldType]: "",
    }))

    if (!availableFields.includes(fieldType)) {
      setAvailableFields((prev) => [...prev, fieldType])
    }
  }

  const handleOverwriteBarcodesChange = (
    checked: boolean | "indeterminate",
  ) => {
    const overwrite = checked === true
    setOverwriteBarcodes(overwrite)
    // Превью и отправляемый снимок должны соответствовать текущему чекбоксу.
    setCardData((previous) =>
      previous.map((card) => {
        const updatedCard = { ...card }
        delete updatedCard.overwriteBarcodes
        if (overwrite && card.barcode) updatedCard.overwriteBarcodes = true
        return updatedCard
      }),
    )
  }

  const addDocument = () => {
    setDocuments((prev) => [...prev, createDocumentBlock()])
  }

  const removeDocument = (blockId: string) => {
    setDocuments((prev) => prev.filter((block) => block.id !== blockId))
    setDocColumnTarget((prev) => (prev?.blockId === blockId ? null : prev))
  }

  const updateDocument = (
    blockId: string,
    patch: Partial<Omit<DocumentBlock, "id">>,
  ) => {
    setDocuments((prev) =>
      prev.map((block) =>
        block.id === blockId ? { ...block, ...patch } : block,
      ),
    )
  }

  /** Слоты для незамапленных полей документа */
  const availableDocFields = (block: DocumentBlock): DocField[] =>
    DOC_FIELDS.filter((field) => {
      if (block.columns[field]) return false
      // Дату окончания не маппим, когда документ помечен бессрочным
      if (field === "endDate" && block.isEndless) return false
      return true
    })

  const removeDocFieldMapping = (blockId: string, field: DocField) => {
    updateDocument(blockId, {
      columns: {
        ...documents.find((b) => b.id === blockId)!.columns,
        [field]: "",
      },
    })
  }

  /** Собирает документы одной строки Excel */
  const buildDocuments = (
    excelData: ExcelData,
    index: number,
  ): CardDocument[] =>
    documents
      .map((block) => {
        const doc: CardDocument = { type: Number(block.type) }

        DOC_FIELDS.forEach((field) => {
          const col = block.columns[field]
          if (!col) return
          // Дата окончания не передаётся для бессрочных документов
          if (field === "endDate" && block.isEndless) return

          const raw = excelData[col]?.[index]
          if (raw === undefined || raw === null || raw === "") return

          if (DOC_DATE_FIELDS.includes(field)) {
            const formatted = formatExcelDate(raw)
            if (formatted) doc[field] = formatted
          } else {
            doc[field] = String(raw)
          }
        })

        // Бессрочный — дата окончания не передаётся вовсе.
        // Если чекбокс снят, но в строке пусто — бэкенд сам проставит isEndless.
        if (block.isEndless) doc.isEndless = true

        return doc
      })
      .filter((doc) => Object.keys(doc).length > 1)

  const generateCardData = () => {
    if (!selectedColumns.article) {
      NotificationService.showNotification({
        message: "Выберите обязательную колонку 'Артикул вб'",
        level: "error",
        success: false,
      })
      return
    }

    if (!excelDataRef.current) return

    const excelData: ExcelData = excelDataRef.current
    const articleData = excelData[selectedColumns.article] || []

    const generatedData: CardData[] = articleData
      .map((article, index) => ({ article, index }))
      .filter(
        ({ article }) =>
          article !== null && article !== undefined && article !== "",
      )
      .map(({ article, index }) => {
        const card: CardData = {
          article: String(article),
        }

        CARD_FIELDS.forEach((field) => {
          const selectedField = selectedColumns?.[field]
          if (selectedField) {
            const fieldData = excelData[selectedField]
            if (
              fieldData &&
              fieldData[index] !== undefined &&
              fieldData[index] !== null &&
              fieldData[index] !== ""
            ) {
              card[field] = String(fieldData[index])
            }
          }
        })

        const docs = buildDocuments(excelData, index)
        if (docs.length) card.documents = docs
        if (excludeDocuments) card.excludeDocuments = true
        // Флаг уходит на бэкенд только когда есть баркод — иначе он ничего не меняет
        if (overwriteBarcodes && card.barcode) card.overwriteBarcodes = true

        return card
      })

    setCardData(generatedData)
  }

  const submitData = async () => {
    if (cardData.length === 0) {
      NotificationService.showNotification({
        message: "Нет данных для отправки",
        level: "error",
        success: false,
      })
      return
    }

    try {
      const response = await BaseApi.updateWbCards(cardData)
      const data = response?.data
      const success = data?.success || false
      NotificationService.showNotification({
        message:
          data?.message || success
            ? "Обновление карточек запущено"
            : "Произошла ошибка при обновлении карточек",
        level: success ? "success" : "error",
        success,
      })
      clearData()
    } catch (error: any) {
      NotificationService.showNotification({
        message: error?.response?.data?.message || "Ошибка при отправке данных",
        level: "error",
        success: false,
      })
    }
  }

  if (wbContentState.updateCardStatus === STATUS.IN_PROGRESS) {
    return (
      <Box p="4" maxWidth="700px" mx="auto">
        <StatusInfo status={wbContentState.updateCardStatus} />
      </Box>
    )
  }

  const hasCardFields = CARD_FIELDS.some((f) => selectedColumns[f])
  const canGenerate =
    !!selectedColumns.article && (hasCardFields || documents.length > 0)

  return (
    <Box p="4" maxWidth="700px" mx="auto">
      <Heading size="5" align="center" mb="5">
        Обновление карточек Wildberries
      </Heading>

      <Flex align="center" justify="center" gap="4" mb="4" wrap="wrap">
        <Text size="2" color="gray">
          Статус последнего обновления:
        </Text>
        <StatusInfo
          status={
            wbContentState.errors?.length
              ? "SUCCESS_WITH_ERROR"
              : (wbContentState.updateCardStatus as any)
          }
        />
        {wbContentState.errors?.length ? (
          <>
            <Button
              variant="outline"
              size="1"
              onClick={() => setModalOpen(true)}
            >
              посмотреть ошибки
            </Button>
            <Modal
              isOpen={isModalOpen}
              onClose={() => setModalOpen(false)}
              onOk={() => setModalOpen(false)}
              content={
                <ObjectViewer
                  data={wbContentState.errors}
                  title="Ошибки при обновлении карточек"
                />
              }
              hideCancelBtn
            />
          </>
        ) : null}
      </Flex>

      <Flex direction="column" gap="3" mb="4">
        {!file && (
          <Button onClick={handleAttachClick} disabled={isLoading} size="3">
            <UploadIcon />
            {isLoading ? "Загрузка..." : "Прикрепить Excel"}
          </Button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          aria-label="Excel-файл карточек"
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />

        {file && (
          <Flex
            align="center"
            justify="between"
            p="2"
            style={{
              backgroundColor: "var(--gray-3)",
              borderRadius: "var(--radius-2)",
            }}
          >
            <Text size="2">Файл: {file.name}</Text>
            <IconButton
              variant="ghost"
              color="red"
              size="1"
              onClick={clearData}
            >
              <Cross1Icon />
            </IconButton>
          </Flex>
        )}
      </Flex>

      {columns.length > 0 && (
        <Card size="2" style={{ marginBottom: 16 }}>
          <Flex direction="column" gap="4">
            <Flex direction="column" gap="1">
              <Text as="label" size="2" weight="medium">
                Артикул вб <Text color="red">*</Text>
              </Text>
              <Select
                value={selectedColumns.article}
                onChange={(value) =>
                  setSelectedColumns((prev) => ({ ...prev, article: value }))
                }
                options={columns.map((column) => ({
                  value: column,
                  label: column,
                }))}
                placeholder="-- Выберите колонку --"
              />
            </Flex>

            {hasCardFields && (
              <Flex
                direction="column"
                gap="2"
                p="3"
                style={{
                  backgroundColor: "var(--green-2)",
                  borderRadius: "var(--radius-2)",
                }}
              >
                <Text size="2" weight="medium" color="green">
                  Изменяемые поля:
                </Text>
                <Flex gap="2" wrap="wrap">
                  {CARD_FIELDS.map((field) => {
                    if (selectedColumns[field]) {
                      return (
                        <Badge key={field} color="green" size="2">
                          <Flex align="center" gap="1">
                            <Text size="1">
                              {getMessage(field)}: {selectedColumns[field]}
                            </Text>
                            <IconButton
                              variant="ghost"
                              color="red"
                              size="1"
                              onClick={() => removeFieldMapping(field)}
                            >
                              <Cross1Icon />
                            </IconButton>
                          </Flex>
                        </Badge>
                      )
                    }
                    return null
                  })}
                </Flex>
              </Flex>
            )}

            {selectedColumns.barcode && (
              <Flex
                direction="column"
                gap="2"
                p="3"
                style={{
                  backgroundColor: "var(--orange-2)",
                  borderRadius: "var(--radius-2)",
                }}
              >
                <Text size="2" weight="medium" color="orange">
                  {getMessage("barcode")}
                </Text>
                <Text as="label" size="2">
                  <Flex gap="2" align="start">
                    <Checkbox
                      checked={overwriteBarcodes}
                      onCheckedChange={handleOverwriteBarcodesChange}
                    />
                    <Flex direction="column">
                      <Text size="2">{getMessage("overwriteBarcodes")}</Text>
                      <Text size="1" color="gray">
                        {getMessage("overwriteBarcodesHint")}
                      </Text>
                    </Flex>
                  </Flex>
                </Text>
              </Flex>
            )}

            {availableFields.length > 0 && !showColumnSelect && (
              <Flex
                direction="column"
                gap="1"
                p="3"
                style={{
                  border: "1px dashed var(--gray-6)",
                  borderRadius: "var(--radius-2)",
                }}
              >
                <Text as="label" size="2" weight="medium">
                  Добавить поле:
                </Text>
                <Select
                  value={selectedFieldType}
                  onChange={handleFieldTypeSelect}
                  options={availableFields.map((field) => ({
                    value: field,
                    label: getMessage(field),
                  }))}
                  placeholder="-- Выберите тип поля --"
                />
              </Flex>
            )}

            {showColumnSelect && (
              <Flex
                direction="column"
                gap="2"
                p="3"
                style={{
                  backgroundColor: "var(--blue-2)",
                  borderRadius: "var(--radius-2)",
                }}
              >
                <Text as="label" size="2" weight="medium" color="blue">
                  Выберите колонку для {getMessage(selectedFieldType)}:
                </Text>
                <Select
                  value=""
                  onChange={handleColumnSelect}
                  options={columns.map((column) => ({
                    value: column,
                    label: column,
                  }))}
                  placeholder="-- Выберите колонку --"
                />
                <Button
                  variant="outline"
                  color="gray"
                  size="1"
                  onClick={() => {
                    setSelectedFieldType("")
                    setShowColumnSelect(false)
                  }}
                >
                  Отмена
                </Button>
              </Flex>
            )}

            {/* ---------- Документы ---------- */}
            <Flex
              direction="column"
              gap="3"
              p="3"
              style={{
                border: "1px dashed var(--gray-6)",
                borderRadius: "var(--radius-2)",
              }}
            >
              <Flex align="center" justify="between" gap="2" wrap="wrap">
                <Text size="2" weight="medium">
                  Документы
                </Text>
                <Button variant="soft" size="1" onClick={addDocument}>
                  <PlusIcon />
                  Добавить документ
                </Button>
              </Flex>

              <Text as="label" size="2">
                <Flex gap="2" align="start">
                  <Checkbox
                    checked={excludeDocuments}
                    onCheckedChange={(value) =>
                      setExcludeDocuments(value === true)
                    }
                  />
                  <Flex direction="column">
                    <Text size="2">Исключить документы из проверки</Text>
                    <Text size="1" color="gray">
                      При включении переданные документы не проверяются и
                      обнуляются на стороне WB
                    </Text>
                  </Flex>
                </Flex>
              </Text>

              {documents.map((block, blockIndex) => {
                const free = availableDocFields(block)
                return (
                  <Card
                    key={block.id}
                    variant="classic"
                    size="2"
                    style={{ borderLeft: "3px solid var(--accent-9)" }}
                  >
                    <Flex direction="column" gap="3">
                      <Flex align="center" justify="between" gap="2">
                        <Text size="2" weight="bold">
                          Документ {blockIndex + 1}
                        </Text>
                        <IconButton
                          variant="ghost"
                          color="red"
                          size="1"
                          onClick={() => removeDocument(block.id)}
                        >
                          <Cross1Icon />
                        </IconButton>
                      </Flex>

                      <Flex direction="column" gap="1">
                        <Text as="label" size="2" weight="medium">
                          {getMessage("documentType")}
                        </Text>
                        <Select
                          value={block.type}
                          onChange={(value) =>
                            updateDocument(block.id, { type: value })
                          }
                          options={WB_DOCUMENT_TYPES}
                          placeholder="-- Выберите тип --"
                        />
                      </Flex>

                      <Text as="label" size="2">
                        <Flex gap="2" align="center">
                          <Checkbox
                            checked={block.isEndless}
                            onCheckedChange={(value) => {
                              const isEndless = value === true
                              updateDocument(block.id, {
                                isEndless,
                                // При включении бессрочности маппинг даты окончания не нужен
                                columns: isEndless
                                  ? { ...block.columns, endDate: "" }
                                  : block.columns,
                              })
                            }}
                          />
                          {getMessage("isEndless")}
                        </Flex>
                      </Text>

                      {Object.entries(block.columns).some(
                        ([field, col]) =>
                          col && !(field === "endDate" && block.isEndless),
                      ) && (
                        <Flex
                          direction="column"
                          gap="2"
                          p="2"
                          style={{
                            backgroundColor: "var(--green-2)",
                            borderRadius: "var(--radius-2)",
                          }}
                        >
                          <Text size="2" weight="medium" color="green">
                            Поля документа:
                          </Text>
                          <Flex gap="2" wrap="wrap">
                            {DOC_FIELDS.map((field) => {
                              const col = block.columns[field]
                              if (!col) return null
                              if (field === "endDate" && block.isEndless)
                                return null
                              return (
                                <Badge key={field} color="green" size="2">
                                  <Flex align="center" gap="1">
                                    <Text size="1">
                                      {docFieldLabel(field)}: {col}
                                    </Text>
                                    <IconButton
                                      variant="ghost"
                                      color="red"
                                      size="1"
                                      onClick={() =>
                                        removeDocFieldMapping(block.id, field)
                                      }
                                    >
                                      <Cross1Icon />
                                    </IconButton>
                                  </Flex>
                                </Badge>
                              )
                            })}
                          </Flex>
                        </Flex>
                      )}

                      {free.length > 0 && (
                        <Flex direction="column" gap="1">
                          <Text as="label" size="2" weight="medium">
                            Добавить поле документа:
                          </Text>
                          <Select
                            value=""
                            onChange={(value) =>
                              setDocColumnTarget({
                                blockId: block.id,
                                field: value as DocField,
                              })
                            }
                            options={free.map((field) => ({
                              value: field,
                              label: docFieldLabel(field),
                            }))}
                            placeholder="-- Выберите поле --"
                          />
                        </Flex>
                      )}

                      {docColumnTarget?.blockId === block.id && (
                        <Flex
                          direction="column"
                          gap="2"
                          p="2"
                          style={{
                            backgroundColor: "var(--blue-2)",
                            borderRadius: "var(--radius-2)",
                          }}
                        >
                          <Text
                            as="label"
                            size="2"
                            weight="medium"
                            color="blue"
                          >
                            Колонка для {docFieldLabel(docColumnTarget.field)}:
                          </Text>
                          <Select
                            value=""
                            onChange={(value) => {
                              if (value) {
                                updateDocument(block.id, {
                                  columns: {
                                    ...block.columns,
                                    [docColumnTarget.field]: value,
                                  },
                                })
                              }
                              setDocColumnTarget(null)
                            }}
                            options={columns.map((column) => ({
                              value: column,
                              label: column,
                            }))}
                            placeholder="-- Выберите колонку --"
                          />
                          <Button
                            variant="outline"
                            color="gray"
                            size="1"
                            onClick={() => setDocColumnTarget(null)}
                          >
                            Отмена
                          </Button>
                        </Flex>
                      )}
                    </Flex>
                  </Card>
                )
              })}

              {excludeDocuments && (
                <Text size="1" color="orange">
                  Внимание: документы, переданные в загрузке, будут обнулены и
                  не проверены на стороне WB
                </Text>
              )}
            </Flex>

            {canGenerate && (
              <Button color="green" size="3" onClick={generateCardData}>
                <PlusIcon />
                Сформировать данные
              </Button>
            )}
          </Flex>
        </Card>
      )}

      {cardData.length > 0 && (
        <Flex direction="column" gap="4">
          <Card size="2">
            <Flex direction="column" gap="3">
              <Heading size="4">Сформированные данные:</Heading>
              <Text size="2" color="gray">
                Всего карточек: {cardData.length}
              </Text>
              <ScrollArea style={{ maxHeight: 300 }}>
                <Flex direction="column" gap="2">
                  {cardData.slice(0, 5).map((card, index) => (
                    <Card
                      key={index}
                      variant="classic"
                      size="1"
                      style={{ borderLeft: "3px solid var(--accent-9)" }}
                    >
                      <Flex direction="column" gap="1">
                        <Flex gap="2">
                          <Text size="2" weight="bold">
                            Артикул:
                          </Text>
                          <Text size="2">{card.article}</Text>
                        </Flex>
                        {CARD_FIELDS.map((field) => {
                          const value = card[field]
                          if (value) {
                            return (
                              <Flex key={field} gap="2">
                                <Text size="2" weight="bold">
                                  {getMessage(field)}:
                                </Text>
                                <Text
                                  size="2"
                                  style={{ wordBreak: "break-all" }}
                                >
                                  {String(value)}
                                </Text>
                              </Flex>
                            )
                          }
                          return null
                        })}

                        {Array.isArray(card.documents) &&
                          card.documents.length > 0 && (
                            <Flex direction="column" gap="1" mt="1">
                              <Text size="2" weight="bold">
                                Документы: {card.documents.length}
                              </Text>
                              {card.documents.map((doc, docIndex) => (
                                <Card
                                  key={docIndex}
                                  variant="surface"
                                  size="1"
                                  style={{ backgroundColor: "var(--gray-2)" }}
                                >
                                  <Flex direction="column" gap="1">
                                    <Text size="1">
                                      {getMessage("documentType")}:{" "}
                                      {WB_DOCUMENT_TYPES.find(
                                        (t) => t.value === String(doc.type),
                                      )?.label || doc.type}
                                    </Text>
                                    {DOC_FIELDS.map((field) => {
                                      const value = doc[field]
                                      if (!value) return null
                                      if (field === "endDate" && doc.isEndless)
                                        return null
                                      return (
                                        <Text key={field} size="1">
                                          {docFieldLabel(field)}:{" "}
                                          {String(value)}
                                        </Text>
                                      )
                                    })}
                                    {doc.isEndless && (
                                      <Text size="1" color="gray">
                                        {getMessage("isEndless")}
                                      </Text>
                                    )}
                                  </Flex>
                                </Card>
                              ))}
                            </Flex>
                          )}

                        {card.excludeDocuments && (
                          <Text size="1" color="orange">
                            {getMessage("excludeDocuments")}
                          </Text>
                        )}

                        {card.overwriteBarcodes && (
                          <Text size="1" color="orange">
                            {getMessage("overwriteBarcodes")}
                          </Text>
                        )}
                      </Flex>
                    </Card>
                  ))}
                  {cardData.length > 5 && (
                    <Text
                      size="2"
                      color="gray"
                      align="center"
                      style={{ fontStyle: "italic" }}
                    >
                      ... и еще {cardData.length - 5} карточек
                    </Text>
                  )}
                </Flex>
              </ScrollArea>
            </Flex>
          </Card>

          <Button size="3" onClick={submitData}>
            Запустить обновление
          </Button>
        </Flex>
      )}
    </Box>
  )
}
